import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Home, Phone, Layers, MapPin } from 'lucide-react';
import { navItems, siteConfig } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getIcon = (label) => {
    switch (label) {
      case 'Home': return <Home className="w-4 h-4" />;
      case 'Services': return <Layers className="w-4 h-4" />;
      case 'Locations': return <MapPin className="w-4 h-4" />;
      case 'Contact': return <Phone className="w-4 h-4" />;
      default: return null;
    }
  };

  const renderNavLink = (item, isMobile = false) => {
    const baseClasses = isMobile
      ? "flex items-center space-x-3 px-4 py-3 rounded-xl text-white/80 hover:text-white hover:bg-white/5 transition-all duration-300"
      : "flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium text-white/80 hover:text-white hover:bg-white/5 transition-all duration-300";

    return (
      <Link
        key={item.label}
        to={item.href}
        onClick={() => isMobile && setIsMobileMenuOpen(false)}
        className={baseClasses}
      >
        {getIcon(item.label)}
        <span className={isMobile ? "font-medium" : ""}>{item.label}</span>
      </Link>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-navy-dark/95 backdrop-blur-lg shadow-lg shadow-black/20'
          : 'bg-transparent'
      }`}
      style={{ backgroundColor: isScrolled ? 'rgba(31, 59, 96, 0.95)' : 'transparent' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src="/logo-original.png"
              alt="Pacific Crest Properties Logo"
              className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
            />
            <div className="hidden sm:block">
              <span className="font-display font-semibold text-lg text-white tracking-tight">
                Pacific Crest
              </span>
              <span className="block text-xs tracking-wider uppercase text-white/60">Properties</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => renderNavLink(item))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            <Link
              to={siteConfig.links.offers}
              className="btn-gold text-sm px-6 py-3"
            >
              Get Offer
            </Link>
            <Link
              to={siteConfig.links.renovations}
              className="btn-outline-gold text-sm px-6 py-3"
            >
              Renovation Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 overflow-hidden transition-all duration-500 ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
        style={{ backgroundColor: 'rgba(31, 59, 96, 0.98)' }}
      >
        <nav className="px-4 py-6 space-y-2">
          {navItems.map((item) => renderNavLink(item, true))}
          <div className="pt-4 space-y-3">
            <Link
              to={siteConfig.links.offers}
              className="btn-gold block text-center w-full"
            >
              Get Offer
            </Link>
            <Link
              to={siteConfig.links.renovations}
              className="btn-outline-gold block text-center w-full"
            >
              Renovation Quote
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
